import type { ClinicData } from '@/lib/clinic/types'

import { STORAGE_KEY, createEmptyData } from '@/lib/clinic/defaults'

/**
 * Where the clinic records live. The app only talks to this interface, so a
 * backend (REST API, database) can replace the browser storage version below
 * without touching any screen.
 */
export interface ClinicRepository {
    /** Saved records, or null when nothing has been saved yet. */
    load: () => Promise<ClinicData | null>
    save: (data: ClinicData) => Promise<void>
    /** Notifies when the records change somewhere else (another tab). Returns an unsubscribe function. */
    subscribe: (onChange: () => void) => () => void
}

/** Fills in anything missing from older saved copies so every screen can rely on the full shape. */
export const normalizeData = (raw: unknown): ClinicData | null => {
    if (!raw || typeof raw !== 'object') return null

    const value = raw as Partial<ClinicData>

    if (!Array.isArray(value.patients)) return null

    const defaults = createEmptyData()
    const settings: Partial<ClinicData['settings']> = value.settings ?? {}

    return {
        version: 1,
        patients: value.patients,
        visits: Array.isArray(value.visits) ? value.visits : [],
        prescriptions: (Array.isArray(value.prescriptions) ? value.prescriptions : []).map(item => ({
            ...item,
            medicines: Array.isArray(item.medicines) ? item.medicines : [],
            printNotes: Boolean(item.printNotes)
        })),
        followUps: Array.isArray(value.followUps) ? value.followUps : [],
        reminders: Array.isArray(value.reminders) ? value.reminders : [],
        treatments: Array.isArray(value.treatments) ? value.treatments : defaults.treatments,
        settings: {
            clinic: { ...defaults.settings.clinic, ...settings.clinic },
            prescription: { ...defaults.settings.prescription, ...settings.prescription },
            followUp: {
                ...defaults.settings.followUp,
                ...settings.followUp,
                templates: { ...defaults.settings.followUp.templates, ...settings.followUp?.templates }
            }
        },
        counters: { ...defaults.counters, ...value.counters },
        meta: { ...defaults.meta, ...value.meta }
    }
}

export const localStorageRepository: ClinicRepository = {
    load: async () => {
        const raw = window.localStorage.getItem(STORAGE_KEY)

        if (!raw) return null

        try {
            const data = normalizeData(JSON.parse(raw))

            if (data) return data
        }
        catch {
            // Falls through to the backup below.
        }

        window.localStorage.setItem(`${STORAGE_KEY}/unreadable-backup`, raw)

        return null
    },
    save: async data => {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    },
    subscribe: onChange => {
        const handler = (event: StorageEvent): void => {
            if (event.key === STORAGE_KEY) onChange()
        }

        window.addEventListener('storage', handler)

        return () => window.removeEventListener('storage', handler)
    }
}
